import { useState, lazy, Suspense } from "react";
import { useTranslation } from "react-i18next";
import { EmptyState, Pagination } from "@/shared/components";
import { Prompt } from "@/shared/ui";
import { useAdvancesListPage, useAdvancesMutations } from "../../hooks";
import { AdvancesFilter, AdvancesPageSkeleton } from "./components";

const AdvancesTable = lazy(() => import("./components/AdvancesTable").then((m) => ({ default: m.default })));
const AdvanceDetailsModal = lazy(() =>
  import("../AdvanceDetailsModal").then((m) => ({ default: m.default })),
);

interface DetailModalData {
  id: number;
  type: "advance" | "trip";
  autoSettle?: boolean;
}

export default function AdvancesPage() {
  const { t } = useTranslation("AdvancesPage");
  const [detailModal, setDetailModal] = useState<DetailModalData | null>(null);

  const {
    data,
    items,
    isLoading,
    isError,
    search,
    setSearch,
    typeFilter,
    setTypeFilter,
    statusFilter,
    setStatusFilter,
    dateRange,
    setDateRange,
    activeFilters,
    handleResetFilters,
    locale,
    pageItems,
    pagination,
  } = useAdvancesListPage();

  const {
    prompt,
    setPrompt,
    issuingId,
    downloadingId,
    isIssuing,
    isDownloadingT3,
    handleIssue,
    handleDownloadT3,
  } = useAdvancesMutations();

  if (isLoading && !data) {
    return (
      <>
        <title>{t("meta.title")}</title>
        <section className="flex-1">
          <div className="flex items-center justify-between pt-1 pb-4 border-b surface-base-stroke mb-5">
            <h1 className="text-display-xs content-base-primary">{t("header.title")}</h1>
          </div>
          <AdvancesPageSkeleton />
        </section>
      </>
    );
  }

  if (isError) {
    return (
      <>
        <title>{t("meta.title")}</title>
        <section className="flex-1">
          <div className="flex items-center justify-between pt-1 pb-4 border-b surface-base-stroke mb-5">
            <h1 className="text-display-xs content-base-primary">{t("header.title")}</h1>
          </div>
          <p className="mt-4 text-body-regular-md content-action-negative">{t("messages.error")}</p>
        </section>
      </>
    );
  }

  return (
    <>
      <title>{t("meta.title")}</title>
      <section className="flex-1">
        <div className="flex items-center justify-between pt-1 pb-4 border-b surface-base-stroke mb-5">
          <h1 className="text-display-xs content-base-primary">{t("header.title")}</h1>
        </div>

        <div className="radius-3xl surface-base-fill p-7">
          <AdvancesFilter
            search={search}
            onSearchChange={setSearch}
            typeFilter={typeFilter}
            onTypeChange={setTypeFilter}
            statusFilter={statusFilter}
            onStatusChange={setStatusFilter}
            dateRange={dateRange}
            onDateRangeChange={setDateRange}
            locale={locale}
            hasActiveFilters={activeFilters}
            onReset={handleResetFilters}
          />

          {items.length === 0 ? (
            <EmptyState
              hasActiveFilters={activeFilters}
              noResultsText={t("table.noResults")}
              noDataText={t("table.noData")}
              noDataHint={t("table.noDataHint")}
            />
          ) : (
            <>
              <Suspense fallback={<AdvancesPageSkeleton variant="tableOnly" />}>
                <AdvancesTable
                  items={pageItems}
                  onOpen={(id, type) => setDetailModal({ id, type })}
                  onIssue={handleIssue}
                  onDownloadT3={handleDownloadT3}
                  onSettle={(entity) => setDetailModal({ id: entity.id, type: entity.type, autoSettle: true })}
                  issuingId={issuingId}
                  downloadingId={downloadingId}
                  isAnyModalOpen={detailModal !== null}
                />
              </Suspense>

              <Pagination
                currentPage={pagination.page}
                totalPages={pagination.totalPages}
                onPageChange={pagination.setPage}
                fromItem={pagination.fromItem}
                toItem={pagination.toItem}
                total={items.length}
              />
            </>
          )}
        </div>
      </section>

      {detailModal !== null && (
        <Suspense fallback={null}>
          <AdvanceDetailsModal
            entityId={detailModal.id}
            entityType={detailModal.type}
            autoOpenSettlement={detailModal.autoSettle}
            onClose={() => setDetailModal(null)}
            onActionSuccess={() => setDetailModal(null)}
          />
        </Suspense>
      )}

      {isDownloadingT3 && (
        <Prompt
          loaderMode={true}
          onClose={() => {}}
          title=""
          text=""
          loaderText={t("messages.downloadingT3")}
          additionalText={t("messages.pleaseWait")}
          namespace="AdvancesPage"
        />
      )}

      {isIssuing && (
        <Prompt
          loaderMode={true}
          onClose={() => {}}
          title=""
          text=""
          loaderText={t("messages.loading")}
          additionalText={t("messages.pleaseWait")}
          namespace="AdvancesPage"
        />
      )}

      {prompt && (
        <Prompt
          title={prompt.title}
          text={prompt.text}
          variant={prompt.variant || "success"}
          onClose={() => setPrompt(null)}
          namespace="AdvancesPage"
        />
      )}
    </>
  );
}
